import { useQuery } from "@tanstack/react-query";
import {
  FETCH_FACILITY_LIST,
  SET_FACILITY_PAGINATION,
} from "constants/ActionTypes";
import usePaginatedQueryWithReduxSync from "hooks/usePaginatedQueryWithReduxSync";
import { httpClient } from "utils/Api";
import { API_URL, pageSizeOptions, QueryKeysEnums } from "utils/Constant";

export function useTestStatusCount({ param = "", options = {} } = {}) {
  const key = [QueryKeysEnums.TEST_STATUS_COUNT, param];
  return useQuery({
    queryKey: key,
    queryFn: async () => {
      const res = await httpClient.get(param);
      return res?.data ?? [];
    },
    ...options,
  });
}

export function useFacilityQuery({ state, lga, options = {} }) {
  return useQuery({
    queryKey: [API_URL.FACILITIES, state, lga],
    queryFn: async () => {
      const resp = await httpClient.get(API_URL.FACILITIES, {
        params: {
          facility_state: state,
          facility_lga: lga,
          page_size: 10_000,
        },
      });
      const _facilityOption = (resp?.data?.data || [])?.map((facility) => ({
        name: facility.facility_name,
        label: `${facility.facility_name} - (${facility.facility_code || "-"})`,
        id: facility.id,
      }));
      return _facilityOption || [];
    },
    enabled: !!state && !!lga,
    retry: 0,
    ...options,
  });
}

export function useFacilities({
  page = 1,
  pageSize = pageSizeOptions[1],
  search,
  ordering,
  options = {},
} = {}) {
  const queryKey = [
    QueryKeysEnums.FACILITIES,
    page,
    pageSize,
    search,
    ordering,
  ];

  return usePaginatedQueryWithReduxSync({
    enabled: true,
    queryKey,
    queryFn: async () => {
      const resp = await httpClient.get(API_URL.FACILITIES, {
        params: {
          page,
          page_size: pageSize,
          search: search || undefined,
          ordering: ordering || undefined,
        },
      });
      return resp?.data ?? [];
    },
    reduxListActionType: FETCH_FACILITY_LIST,
    reduxPaginationActionType: SET_FACILITY_PAGINATION,
    emptyList: [],
    ...options,
  });
}


export function useTierDashboard({ key, url, params, options = {} }) {
  return useQuery({
    queryKey: key,
    queryFn: async () => {
      const res = await httpClient.get(url, { params });
      return res?.data ?? [];
    },
    enabled: !!url,
    retry: 0,
    ...options,
  });
}


export function useGetUserLocationLookup({ options = {} }) {
  return useQuery({
    queryKey: [API_URL.DASHBOARD.USER_LOCATION_LOOKUP],
    queryFn: async () => {
      const res = await httpClient.get(API_URL.DASHBOARD.USER_LOCATION_LOOKUP);
      return res?.data ?? [];
    },
    retry: 0,
    ...options,
  });
}

export function useDashboardFilters({ options = {} }) {
  return useQuery({
    queryKey: [QueryKeysEnums.DASHBOARD_FILTERS],
    queryFn: async () => {
      const res = await httpClient.get(API_URL.DASHBOARD.DASHBOARD_FILTER);
      return res?.data ?? {};
    },
    retry: 0,
    ...options,
  });
}
